import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Info } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { CascadeTargetResolutionDialog } from '@/components/blocks/budget/cascade-target-resolution-dialog';
import type { BudgetRevisionSettings } from '@/types/resource-budget-settings.types';
import type { RateChangeBudgetImpact, RateChangeCascadeDecision } from '@/services/rateChangeCascade.service';
import type { ProjectType } from '@/types';

interface RateChangeCascadeDialogProps {
  open: boolean;
  impact: RateChangeBudgetImpact;
  cascadeSettings: BudgetRevisionSettings;
  isSaving?: boolean;
  projectType?: ProjectType;
  isCostCentreMode?: boolean;
  onConfirm: (decision: RateChangeCascadeDecision) => void;
  onCancel: () => void;
}

/**
 * Prompt shown after project / person rate overrides are saved from the Rate
 * Card, offering the same Labour cascade choices as a Resource Budget revision.
 */
export function RateChangeCascadeDialog({
  open,
  impact,
  cascadeSettings,
  isSaving = false,
  projectType,
  isCostCentreMode = false,
  onConfirm,
  onCancel,
}: RateChangeCascadeDialogProps) {
  const salesCascadeAllowed = projectType === 'time-and-materials';

  const [updateCostLabour, setUpdateCostLabour] = useState(cascadeSettings.autoUpdateCostLabour);
  const [updateSalesLabour, setUpdateSalesLabour] = useState(
    cascadeSettings.autoUpdateSalesLabour && salesCascadeAllowed
  );

  const [showTargetResolution, setShowTargetResolution] = useState(false);
  const [pendingDecision, setPendingDecision] = useState<RateChangeCascadeDecision | null>(null);

  const orgDefaultsBothOn =
    cascadeSettings.autoUpdateCostLabour && cascadeSettings.autoUpdateSalesLabour;
  const neitherSelected = !updateCostLabour && !updateSalesLabour;

  const handleConfirm = () => {
    const decision: RateChangeCascadeDecision = {
      updateCostLabour,
      updateSalesLabour,
      overriddenByUser:
        updateCostLabour !== cascadeSettings.autoUpdateCostLabour ||
        updateSalesLabour !== (cascadeSettings.autoUpdateSalesLabour && salesCascadeAllowed),
    };

    if (neitherSelected || isCostCentreMode) {
      onConfirm(decision);
      return;
    }

    setPendingDecision(decision);
    setShowTargetResolution(true);
  };

  const handleTargetResolved = (targetCategoryId: number, targetCategoryName: string) => {
    if (!pendingDecision) return;
    setShowTargetResolution(false);
    setPendingDecision(null);
    onConfirm({
      ...pendingDecision,
      cascadeTargetCategoryId: targetCategoryId,
      cascadeTargetCategoryName: targetCategoryName,
    });
  };

  const handleTargetCancelled = () => {
    setShowTargetResolution(false);
    setPendingDecision(null);
  };

  const renderTotals = (previous: number, next: number) => (
    <p className="text-xs text-muted-foreground">
      {formatCurrency(previous)} → <span className="font-medium text-foreground">{formatCurrency(next)}</span>
    </p>
  );

  return (
    <>
      <Dialog open={open && !showTargetResolution} onOpenChange={(o) => !o && onCancel()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Update Budgets for New Rates?</DialogTitle>
            <DialogDescription>
              The saved rates change the value of the budgeted Resource Budget hours. Choose which
              Labour budget lines should be updated to match.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-lg border p-3 space-y-1.5 bg-muted/30">
              {impact.affectedRoles.map(role => (
                <div key={role.roleName} className="flex items-center justify-between text-sm">
                  <span className="font-medium">{role.roleName}</span>
                  <span className="text-muted-foreground text-xs">
                    {role.budgetHours} hrs · Cost {formatCurrency(role.previousCostRate)} →{' '}
                    {formatCurrency(role.newCostRate)} · Bill {formatCurrency(role.previousBillRate)} →{' '}
                    {formatCurrency(role.newBillRate)}
                  </span>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-md border p-3">
                <Checkbox
                  id="rate-cascade-cost"
                  checked={updateCostLabour}
                  onCheckedChange={(c) => setUpdateCostLabour(c as boolean)}
                />
                <div className="flex-1 space-y-0.5">
                  <Label htmlFor="rate-cascade-cost" className="cursor-pointer font-medium text-sm">
                    Update Labour in Cost Budget
                  </Label>
                  {renderTotals(impact.previousCostLabour, impact.newCostLabour)}
                  <p className="text-xs text-muted-foreground">
                    Org default: {cascadeSettings.autoUpdateCostLabour ? 'On' : 'Off'}
                  </p>
                </div>
              </div>

              <div className={`flex items-start gap-3 rounded-md border p-3 ${!salesCascadeAllowed ? 'opacity-50' : ''}`}>
                <Checkbox
                  id="rate-cascade-sales"
                  checked={updateSalesLabour}
                  onCheckedChange={(c) => setUpdateSalesLabour(c as boolean)}
                  disabled={!salesCascadeAllowed}
                />
                <div className="flex-1 space-y-0.5">
                  <Label
                    htmlFor="rate-cascade-sales"
                    className={`font-medium text-sm ${salesCascadeAllowed ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                  >
                    Update Labour in Sales Budget
                  </Label>
                  {salesCascadeAllowed ? (
                    <>
                      {renderTotals(impact.previousSalesLabour, impact.newSalesLabour)}
                      <p className="text-xs text-muted-foreground">
                        Org default: {cascadeSettings.autoUpdateSalesLabour ? 'On' : 'Off'}
                      </p>
                    </>
                  ) : (
                    <div className="flex items-center gap-1 mt-0.5">
                      <Info className="h-3 w-3 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">
                        Sales Budget cascade is only available for Time &amp; Materials projects.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {orgDefaultsBothOn && neitherSelected && (
              <Alert className="border-warning-400 bg-warning-50 dark:bg-warning-950">
                <AlertTriangle className="h-4 w-4 text-warning-600" />
                <AlertDescription className="text-warning-800 dark:text-warning-200 text-sm">
                  Labour budgets will not be updated. Your org settings recommend updating both.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onCancel} disabled={isSaving}>
              Not Now
            </Button>
            <Button onClick={handleConfirm} disabled={isSaving}>
              {isSaving ? 'Updating…' : 'Update Budgets'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CascadeTargetResolutionDialog
        open={showTargetResolution}
        cascadeSettings={cascadeSettings}
        onResolved={handleTargetResolved}
        onCancel={handleTargetCancelled}
      />
    </>
  );
}
